import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Form from "../common/Form";
import Button from "../common/Button";
import thumbnailPlaceholder from "../../assets/images/thumbnail-placeholder.png";

const EditableNewsItem = ({
  news,
  isSelected,
  onToggleSelect,
  onEdit,
  isCheckboxDisabled,
  videoRestrictions,
}) => {
  const isTitleAvailable = !!videoRestrictions["news[].description"];
  const isTitleValid = isTitleAvailable
    ? news.title?.length <= videoRestrictions["news[].description"]?.maxLength
    : true;

  const isSpotTitleAvailable = !!videoRestrictions["news[].title"];
  const isSpotTitleValid = isSpotTitleAvailable
    ? news.spotTitle?.length <= videoRestrictions["news[].title"]?.maxLength
    : true;

  const isVoiceTextAvailable = !!videoRestrictions["news[].voiceText"];

  const isVoiceTextValid = isVoiceTextAvailable
    ? news.voiceText?.length <= videoRestrictions["news[].voiceText"]?.maxLength
    : true;

  const isCategoryAvailable = !!videoRestrictions["news[].category"];

  const isCategoryValid = isCategoryAvailable
    ? news.category?.length <= videoRestrictions["news[].category"]?.maxLength
    : true;

  const isImageAvailable = !!videoRestrictions["news[].image"];
  const isImageValid = isImageAvailable;

  const isValid =
    isTitleValid &&
    isSpotTitleValid &&
    isVoiceTextValid &&
    isImageValid &&
    isCategoryValid;

  return (
    <div
      className={`bg-white border rounded-lg p-4 mb-3 hover:shadow-md transition-shadow ${
        isSelected ? "ring-2 ring-blue-500 bg-blue-50" : ""
      }`}
    >
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          <input
            className="pr-5 cursor-pointer"
            disabled={(isCheckboxDisabled && !isSelected) || !isValid}
            type="checkbox"
            value={news.id}
            onChange={() => onToggleSelect(news.id)}
            checked={isSelected}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-2">
            {isImageAvailable && (
              <div className="relative">
                <img
                  src={news.image}
                  alt="News thumbnail"
                  className="w-32 h-24 object-cover rounded"
                  onError={(e) => {
                    e.target.src = thumbnailPlaceholder;
                  }}
                />
                <input
                  type="file"
                  accept="image/*"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        onEdit(news.id, "image", event.target.result);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  title="Click to replace image"
                />
                <div className="absolute bottom-0 right-0 bg-blue-500 text-white text-xs px-1 rounded-tl">
                  📷
                </div>
              </div>
            )}
            {isTitleAvailable && (
              <div className="flex-1">
                <Form.FormTextInput
                  label="News title"
                  name={`title-${news.id}`}
                  value={news.title}
                  onChange={(e) => onEdit(news.id, "title", e.target.value)}
                  maxLength={videoRestrictions["news[].description"]?.maxLength}
                  className="text-sm font-semibold bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
                  placeholder="News title"
                  error={
                    !news.title?.length
                      ? "Title is required"
                      : news.title?.length >
                        videoRestrictions["news[].description"]?.maxLength
                      ? "Title is too long"
                      : ""
                  }
                />
                <div className="text-xs text-gray-500 mt-1">
                  {news.title?.length || 0} /{" "}
                  {videoRestrictions["news[].description"]?.maxLength}
                </div>
              </div>
            )}
          </div>

          {isSpotTitleAvailable && (
            <div className="mb-2">
              <Form.FormTextInput
                label="Spot title"
                name={`spotTitle-${news.id}`}
                value={news.spotTitle}
                onChange={(e) => onEdit(news.id, "spotTitle", e.target.value)}
                maxLength={videoRestrictions["news[].title"]?.maxLength}
                className="text-xs bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
                placeholder="Spot title"
                error={
                  !news.spotTitle?.length
                    ? "Spot title is required"
                    : news.spotTitle?.length >
                      videoRestrictions["news[].title"]?.maxLength
                    ? "Spot title is too long"
                    : ""
                }
              />
              <div className="text-xs text-gray-500 mt-1">
                {news.spotTitle?.length || 0} /{" "}
                {videoRestrictions["news[].title"]?.maxLength}
              </div>
            </div>
          )}

          {isCategoryAvailable && (
            <div className="mb-2">
              <Form.FormTextInput
                label="Category"
                name={`category-${news.id}`}
                value={news.category}
                onChange={(e) => onEdit(news.id, "category", e.target.value)}
                maxLength={videoRestrictions["news[].category"]?.maxLength}
                className="text-xs bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
                placeholder="Category"
                error={
                  !news.category?.length
                    ? "Category is required"
                    : news.category?.length >
                      videoRestrictions["news[].category"]?.maxLength
                    ? "Category is too long"
                    : ""
                }
              />
              <div className="text-xs text-gray-500 mt-1">
                {news.category?.length || 0} /{" "}
                {videoRestrictions["news[].category"]?.maxLength}
              </div>
            </div>
          )}

          {isVoiceTextAvailable && (
            <>
              <Form.Textarea
                label="Voice text"
                name={`voiceText-${news.id}`}
                value={news.voiceText}
                onChange={(e) => onEdit(news.id, "voiceText", e.target.value)}
                className="text-xs text-gray-700 bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1 resize-none"
                placeholder="Voice text"
                error={
                  !news.voiceText?.length
                    ? "Voice text is required"
                    : news.voiceText?.length >
                      videoRestrictions["news[].voiceText"]?.maxLength
                    ? "Voice text is too long"
                    : ""
                }
              />
              <div className="text-xs text-gray-600">
                {news.voiceText?.length} /{" "}
                {videoRestrictions["news[].voiceText"]?.maxLength}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const SortableNewsItem = ({ news, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: news.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white flex items-center row justify-between gap-2 border rounded-lg p-3 mb-2 cursor-move hover:shadow-md transition-shadow"
    >
      <div
        className="flex items-center space-x-3"
        {...attributes}
        {...listeners}
      >
        <img
          src={news.image}
          alt="News thumbnail"
          className="w-12 h-9 object-cover rounded"
          onError={(e) => {
            e.target.src = thumbnailPlaceholder;
          }}
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-900 truncate">
            {news.title}
          </div>
          <div className="text-xs text-gray-600 truncate">{news.spotTitle}</div>
        </div>
      </div>

      <Button color="danger" onClick={onRemove}>
        ×
      </Button>
    </div>
  );
};

// Export both components
export { EditableNewsItem, SortableNewsItem };
export default SortableNewsItem;
